import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import VoiceEnquiryPanel from "./VoiceEnquiryPanel";
import { mergeParsedEnquiryFields } from "../../utils/parseEnquiryTranscript";

const EMPTY_FORM = {
  customerName: "",
  businessName: "",
  address: "",
  city: "",
  pincode: "",
  service: "",
  notes: "",
  reminderDate: "",
  reminderTime: "",
  enquirySource: "",
  customerType: "",
  mobile: "",
  email: "",
  loanAmount: "",
  loanType: "",
};

const EnquiryFormModal = ({
  showEnquiryModal,
  closeModal,
  initialMode = "manual",
}) => {
  const [entryMode, setEntryMode] = useState(initialMode);
  const [customerName, setCustomerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [service, setService] = useState("");
  const [notes, setNotes] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [enquirySource, setEnquirySource] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [loanType, setLoanType] = useState("");
  const [loadButton, setLoadButton] = useState(false);
  const [usedVoice, setUsedVoice] = useState(false);

  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const resetForm = () => {
    setCustomerName(EMPTY_FORM.customerName);
    setBusinessName(EMPTY_FORM.businessName);
    setAddress(EMPTY_FORM.address);
    setCity(EMPTY_FORM.city);
    setPincode(EMPTY_FORM.pincode);
    setService(EMPTY_FORM.service);
    setNotes(EMPTY_FORM.notes);
    setReminderDate(EMPTY_FORM.reminderDate);
    setReminderTime(EMPTY_FORM.reminderTime);
    setEnquirySource(EMPTY_FORM.enquirySource);
    setCustomerType(EMPTY_FORM.customerType);
    setMobile(EMPTY_FORM.mobile);
    setEmail(EMPTY_FORM.email);
    setLoanAmount(EMPTY_FORM.loanAmount);
    setLoanType(EMPTY_FORM.loanType);
    setUsedVoice(false);
    setEntryMode(initialMode);
  };

  useEffect(() => {
    if (showEnquiryModal) {
      setEntryMode(initialMode);
    } else {
      resetForm();
    }
  }, [showEnquiryModal, initialMode]);

  const getFormSnapshot = () => ({
    customerName,
    businessName,
    address,
    city,
    pincode,
    service,
    notes,
    reminderDate,
    reminderTime,
    enquirySource,
    customerType,
    mobile,
    email,
    loanAmount,
    loanType,
  });

  const applyVoiceFields = (parsed, { usedVoice: voiceUsed = false } = {}) => {
    const merged = mergeParsedEnquiryFields(getFormSnapshot(), parsed, {
      overwriteEmptyOnly: true,
    });

    setCustomerName(merged.customerName);
    setBusinessName(merged.businessName);
    setAddress(merged.address);
    setCity(merged.city);
    setPincode(merged.pincode);
    setNotes(merged.notes || parsed.rawTranscript || notes);
    setEnquirySource(merged.enquirySource || (voiceUsed ? "Voice Inquiry" : enquirySource));
    setCustomerType(merged.customerType || customerType);
    setMobile(merged.mobile || mobile);
    setEmail(merged.email || email);
    setUsedVoice(voiceUsed || usedVoice);

    toast.info("Voice details applied. Review the form and submit.", {
      position: "top-center",
      autoClose: 2500,
    });
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadButton(true);
    try {
      const response = await fetch(`${url}/leads/admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: customerName,
          mobile: mobile,
          business_name: businessName,
          address: address,
          city: city,
          pincode: pincode,
          type: service,
          remainder_date: reminderDate,
          remainder_time: reminderTime,
          type_customer: customerType,
          remark: notes,
          email: email,
          source: enquirySource || (usedVoice ? "Voice Inquiry" : ""),
          assigned_to: isLoggedIn.full_name,
          property_id: isLoggedIn.main_id,
          enquiry_source: loanType,
          borrowAmount: loanAmount,
        }),
      });
      const res = await response.json();
      if (response.ok) {
        toast.success("Enquiry added successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
        handleClose();
      } else {
        toast.error(`${res.message}`, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(`${error}`, {
        position: "top-center",
        autoClose: 10000,
      });
    } finally {
      setLoadButton(false);
    }
  };

  if (!showEnquiryModal) return null;

  return (
    <div className="ls-enquiry-modal">
      <div className="ls-enquiry-modal-content">
        <div className="quotation-header">
          <h2><i className="fa-solid fa-envelope"></i> New Enquiry</h2>
          <button type="button" className="quotation-close" onClick={handleClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="enquiry-entry-tabs" role="tablist" aria-label="Enquiry entry mode">
          <button
            type="button"
            role="tab"
            aria-selected={entryMode === "manual"}
            className={`enquiry-entry-tabs__btn${entryMode === "manual" ? " is-active" : ""}`}
            onClick={() => setEntryMode("manual")}
          >
            Manual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={entryMode === "voice"}
            className={`enquiry-entry-tabs__btn${entryMode === "voice" ? " is-active" : ""}`}
            onClick={() => setEntryMode("voice")}
          >
            <i className="fa-solid fa-microphone" aria-hidden="true" /> Voice
          </button>
        </div>

        {usedVoice && entryMode === "manual" && (
          <div className="enquiry-voice-badge">
            <i className="fa-solid fa-microphone-lines" aria-hidden="true" />
            Filled from voice — please review before submit
          </div>
        )}

        {entryMode === "voice" ? (
          <VoiceEnquiryPanel
            onApplyFields={applyVoiceFields}
            onSwitchToManual={() => setEntryMode("manual")}
          />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="ls-form-group">
              <label>Customer/Business Owner Name</label>
              <input
                type="text"
                name="customerName"
                placeholder="Enter full name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>
            <div className="ls-form-group">
              <label>Mobile Number*</label>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                name="mobile"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/\D/g, "");
                  if (value.length <= 10) {
                    if (value === "" || /^[6-9]/.test(value)) {
                      setMobile(value);
                    }
                  }
                }}
                required
              />
              {mobile.length > 0 && mobile.length < 10 && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  Mobile number must be 10 digits
                </p>
              )}
            </div>

            <div className="ls-form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="ls-form-group">
              <label>Business Name/Company Name</label>
              <input
                type="text"
                name="businessName"
                placeholder="Enter business name/ Company Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </div>

            <div className="ls-form-group">
              <label>Address</label>
              <input
                type="text"
                name="address"
                placeholder="Enter address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="ls-form-row">
              <div className="ls-form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  placeholder="Enter city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>

              <div className="ls-form-group">
                <label>Pincode</label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="pincode"
                  placeholder="Enter pincode"
                  value={pincode}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,6}$/.test(value)) {
                      setPincode(value);
                    }
                  }}
                />
              </div>
            </div>

            <div className="ls-form-group">
              <label>Add Note</label>
              <textarea
                name="notes"
                placeholder="Additional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="ls-form-group">
              <label>Enquiry Source</label>
              <input
                type="text"
                name="enquirySource"
                placeholder="Where did you hear about us?"
                value={enquirySource}
                onChange={(e) => setEnquirySource(e.target.value)}
              />
            </div>

            <div className="ls-form-group">
              <label>Type of Customer</label>
              <div className="ls-radio-group">
                <label
                  className={`ls-radio-option ${customerType === "Business" ? "selected" : ""}`}
                  onClick={() => setCustomerType("Business")}
                >
                  <input
                    type="radio"
                    name="customerType"
                    value="Business"
                    checked={customerType === "Business"}
                    readOnly
                  />{" "}
                  Business
                </label>
                <label
                  className={`ls-radio-option ${customerType === "Salaried" ? "selected" : ""}`}
                  onClick={() => setCustomerType("Salaried")}
                >
                  <input
                    type="radio"
                    name="customerType"
                    value="Salaried"
                    checked={customerType === "Salaried"}
                    readOnly
                  />{" "}
                  Salaried
                </label>
              </div>
            </div>

            <div className="ls-modal-action">
              <button type="button" className="ls-modal-cancel" onClick={handleClose}>
                Cancel
              </button>
              {loadButton ? (
                <button type="button" className="ls-apply-btn">
                  Submiting...
                </button>
              ) : (
                <button type="submit" className="ls-apply-btn">
                  Submit
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EnquiryFormModal;
